import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Star, Copy, RefreshCw, ExternalLink, Upload, Loader2,
  TrendingUp, TrendingDown, Minus, ChevronRight,
} from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip,
} from "recharts";
import { copyToClipboard } from "@/lib/clipboard";

const STAR_COLORS = ["#dc2626", "#f97316", "#facc15", "#84cc16", "#10b981"];

// Module-scope chart-config refs — avoid reallocating on every render.
const PR_CHART_MARGIN = { top: 4, right: 12, left: 0, bottom: 0 };
const PR_TOOLTIP_STYLE = { borderRadius: 2, border: "1px solid #E4E4E7", fontSize: 12 };
const PR_BAR_RADIUS = [2, 2, 0, 0];
const PR_RATING_DOMAIN = [1, 5];
const PR_RATING_TICKS = [1, 2, 3, 4, 5];
const PR_RATING_DOT = { r: 2, fill: "#f59e0b" };

// Date-range presets (same UX as the OpenAI Ads page).
function todayYMD() { return new Date().toISOString().slice(0, 10); }
function ymd(d) { return d.toISOString().slice(0, 10); }
function monthsAgoYMD(n) {
  const d = new Date(); d.setMonth(d.getMonth() - n);
  return ymd(new Date(d.getFullYear(), d.getMonth(), 1));
}
const PRESETS = {
  this_month:  { label: "This month",    compute: () => {
    const d = new Date();
    return [ymd(new Date(d.getFullYear(), d.getMonth(), 1)), todayYMD()];
  } },
  last_3:      { label: "Last 3 months",  compute: () => [monthsAgoYMD(2),  todayYMD()] },
  last_6:      { label: "Last 6 months",  compute: () => [monthsAgoYMD(5),  todayYMD()] },
  last_12:     { label: "Last 12 months", compute: () => [monthsAgoYMD(11), todayYMD()] },
  ytd:         { label: "Year-to-date",   compute: () => {
    const d = new Date();
    return [`${d.getFullYear()}-01-01`, todayYMD()];
  } },
  all:         { label: "All time",       compute: () => ["2010-01-01", todayYMD()] },
  custom:      { label: "Custom…",        compute: null },
};

export default function ClientProductReviewPage() {
  const { clientId } = useParams();
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [datePreset, setDatePreset] = useState("last_12");
  const [startDate, setStartDate] = useState(monthsAgoYMD(11));
  const [endDate, setEndDate] = useState(todayYMD());

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, st] = await Promise.all([
        api.get(`/clients/${clientId}/productreview/settings`).then((r) => r.data),
        api.get(`/clients/${clientId}/productreview/stats`,
          { params: { start_date: startDate, end_date: endDate } }).then((r) => r.data),
      ]);
      setSettings(s); setStats(st);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [clientId, startDate, endDate]);

  const applyPreset = (k) => {
    setDatePreset(k);
    const p = PRESETS[k];
    if (p && p.compute) {
      const [s, e] = p.compute();
      setStartDate(s); setEndDate(e);
    }
  };

  const rotate = async () => {
    if (!window.confirm("Rotate the webhook token? You'll need to update the URL inside the ProductReview Brand Management dashboard.")) return;
    await api.post(`/clients/${clientId}/productreview/rotate`);
    toast.success("Token rotated"); await refresh();
  };

  const importCsv = async (e) => {
    e?.preventDefault?.();
    if (!file) { toast.error("Pick a CSV file first"); return; }
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/clients/${clientId}/productreview/import-csv`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setImportResult(data);
      toast.success(`Imported ${data.inserted} new reviews · ${data.duplicate} duplicates · ${data.months_aggregated.length} months`);
      setFile(null); await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  if (loading || !settings || !stats) {
    return <div className="p-8 text-sm text-zinc-500">Loading…</div>;
  }

  const fullUrl = settings.webhook_url.startsWith("http")
    ? settings.webhook_url
    : `${window.location.origin}${settings.webhook_url}`;

  const maxBucket = Math.max(1, ...Object.values(stats.distribution));

  return (
    <div className="space-y-6" data-testid="productreview-page">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 grid place-items-center rounded-sm bg-[#F02D34] text-white font-display font-extrabold">PR</div>
        <div>
          <div className="label-overline">Reviews</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">ProductReview.com.au</h1>
          <p className="text-xs text-zinc-600 mt-1 max-w-2xl">
            Webhook-based ingestion. Every approved review is pushed here in real time.
            Requires the <b>Premium Tier Brand Management</b> plan on productreview.com.au.
          </p>
        </div>
      </div>

      {/* Setup card */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="font-display font-bold text-lg">Webhook endpoint</div>
              <p className="text-xs text-zinc-500">
                Paste this URL into ProductReview → Integrations → Webhooks.
              </p>
            </div>
            <Button size="sm" variant="ghost" className="rounded-sm text-red-600"
              onClick={rotate} data-testid="pr-rotate">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Rotate token
            </Button>
          </div>
          <div className="flex gap-2">
            <Input value={fullUrl} readOnly
              className="rounded-sm font-mono text-[11px] h-9 bg-zinc-50"
              onFocus={(e) => e.target.select()}
              data-testid="pr-webhook-url" />
            <Button size="sm" variant="outline" className="rounded-sm"
              onClick={() => { copyToClipboard(fullUrl); toast.success("Copied"); }}
              data-testid="pr-copy-url">
              <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
            </Button>
          </div>
          <a href="https://support.productreview.com.au/hc/en-us/articles/13431614567311"
            target="_blank" rel="noreferrer"
            className="text-[11px] text-[#002FA7] hover:underline inline-flex items-center gap-1">
            ProductReview setup guide <ExternalLink className="h-3 w-3" />
          </a>
        </CardContent>
      </Card>

      {/* CSV importer for historical reviews */}
      <Card className="rounded-sm border-zinc-200" data-testid="pr-csv-card">
        <CardContent className="p-5 space-y-3">
          <div>
            <div className="font-display font-bold text-lg flex items-center gap-2">
              <Upload className="h-4 w-4" /> Historical CSV import
            </div>
            <p className="text-xs text-zinc-500">
              ProductReview&apos;s webhook only delivers reviews going forward.
              Use this to backfill: export reviews from <b>Brand Management →
              Reviews → Export</b> and drop the CSV here. Re-uploads are safe —
              duplicates skipped via deterministic hash.
            </p>
          </div>
          <form onSubmit={importCsv} className="flex items-center gap-2 flex-wrap">
            <Input type="file" accept=".csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="rounded-sm h-9 max-w-[360px] file:mr-2 file:rounded-sm file:border-0 file:bg-zinc-100 file:px-2 file:py-1 file:text-xs"
              data-testid="pr-csv-input" />
            <Button type="submit" disabled={!file || importing}
              className="rounded-sm bg-[#F02D34] hover:bg-[#c41822] text-white"
              data-testid="pr-csv-submit">
              {importing
                ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                : <Upload className="h-3.5 w-3.5 mr-1.5" />}
              {importing ? "Importing…" : "Import CSV"}
            </Button>
          </form>
          {importResult && (
            <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-[12px]"
              data-testid="pr-csv-result">
              <div className="font-semibold text-emerald-800">
                Imported {importResult.inserted} reviews · {importResult.duplicate} duplicates · {importResult.skipped} skipped
              </div>
              <div className="text-emerald-700 mt-0.5">
                Aggregated months: {importResult.months_aggregated.slice(0, 6).join(", ")}
                {importResult.months_aggregated.length > 6 && ` (+${importResult.months_aggregated.length - 6} more)`}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Date range + summary */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="label-overline">Summary</div>
              <h2 className="font-display font-bold text-xl mt-0.5">Performance snapshot</h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select value={datePreset} onChange={(e) => applyPreset(e.target.value)}
                className="h-9 rounded-sm border border-zinc-200 bg-white px-2 text-xs font-mono w-[150px]"
                data-testid="pr-date-preset">
                {Object.entries(PRESETS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              {datePreset === "custom" ? (
                <>
                  <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                    className="rounded-sm h-9 w-[150px] font-mono text-xs"
                    data-testid="pr-start-date" />
                  <span className="text-xs text-zinc-400">→</span>
                  <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                    className="rounded-sm h-9 w-[150px] font-mono text-xs"
                    data-testid="pr-end-date" />
                </>
              ) : (
                <span className="font-mono text-[10px] text-zinc-500"
                  data-testid="pr-date-display">
                  {startDate} → {endDate}
                </span>
              )}
            </div>
          </div>

          {/* Top-line KPIs with trend indicator */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <KPI label="Reviews in range" value={stats.count}
              delta={stats.count - (stats.previous_window?.count || 0)}
              prev={stats.previous_window?.count || 0}
              testid="pr-kpi-count" />
            <KPI label="Average rating"
              value={stats.avg_rating ? stats.avg_rating.toFixed(2) : "—"}
              delta={stats.previous_window?.avg_rating
                ? (stats.avg_rating - stats.previous_window.avg_rating).toFixed(2)
                : null}
              prev={stats.previous_window?.avg_rating || 0}
              icon={<Star className="h-4 w-4 text-amber-500 fill-amber-500" />}
              isRating
              testid="pr-kpi-avg" />
            <KPI label="5-star %"
              value={stats.count
                ? `${Math.round((stats.distribution.star_5 / stats.count) * 100)}%`
                : "—"}
              testid="pr-kpi-5star" />
            <KPI label="1-2 star %"
              value={stats.count
                ? `${Math.round(((stats.distribution.star_1 + stats.distribution.star_2) / stats.count) * 100)}%`
                : "—"}
              testid="pr-kpi-lowstar" />
          </div>

          {/* Trend indicator banner */}
          {stats.previous_window?.count > 0 && (
            <TrendBanner
              currentAvg={stats.avg_rating}
              previousAvg={stats.previous_window.avg_rating}
              currentCount={stats.count}
              previousCount={stats.previous_window.count}
            />
          )}

          {/* Monthly trend chart */}
          {stats.monthly_series?.length > 0 && (
            <div className="pt-2" data-testid="pr-monthly-chart">
              <div className="label-overline mb-2">
                Monthly trend · {stats.monthly_series.length} month{stats.monthly_series.length === 1 ? "" : "s"}
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={stats.monthly_series}
                  margin={PR_CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="month" stroke="#71717A" fontSize={10} />
                  <YAxis yAxisId="left" stroke="#71717A" fontSize={11} />
                  <YAxis yAxisId="right" orientation="right"
                    stroke="#f59e0b" fontSize={11} domain={PR_RATING_DOMAIN} ticks={PR_RATING_TICKS} />
                  <Tooltip contentStyle={PR_TOOLTIP_STYLE} />
                  <Bar yAxisId="left" dataKey="count" name="Reviews"
                    fill="#F02D34" radius={PR_BAR_RADIUS} maxBarSize={32} />
                  <Line yAxisId="right" type="monotone" dataKey="avg_rating"
                    name="Avg rating" stroke="#f59e0b" strokeWidth={2}
                    dot={PR_RATING_DOT} />
                </ComposedChart>
              </ResponsiveContainer>
              <div className="flex gap-4 text-[11px] font-mono text-zinc-500 mt-1">
                <span><span className="inline-block w-3 h-3 bg-[#F02D34] rounded-sm mr-1 align-middle" />Reviews (bars · left axis)</span>
                <span><span className="inline-block w-3 h-3 bg-[#f59e0b] rounded-sm mr-1 align-middle" />Avg rating (line · right axis, 1-5)</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Star distribution */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5">
          <div className="label-overline mb-3">Star distribution (this month)</div>
          <div className="space-y-1.5">
            {[5, 4, 3, 2, 1].map((s) => {
              const v = stats.distribution[`star_${s}`] || 0;
              const pct = Math.round((v / maxBucket) * 100);
              return (
                <div key={s} className="grid grid-cols-[60px_1fr_60px] items-center gap-2"
                  data-testid={`pr-star-${s}`}>
                  <div className="font-mono text-xs flex items-center gap-1">
                    {s}<Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                  </div>
                  <div className="h-3 bg-zinc-100 rounded-sm overflow-hidden">
                    <div className="h-full transition-all"
                      style={{ width: `${pct}%`, backgroundColor: STAR_COLORS[s - 1] }} />
                  </div>
                  <div className="font-mono text-xs tabular-nums text-right">{v}</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Channel attribution */}
      {Object.keys(stats.channels).length > 0 && (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-5">
            <div className="label-overline mb-3">Channels (all-time)</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.channels).map(([k, v]) => (
                <Badge key={k} variant="outline"
                  className="rounded-sm font-mono text-[11px]"
                  data-testid={`pr-channel-${k}`}>
                  {k} · {v}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent reviews */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5">
          <div className="label-overline mb-3">Recent reviews</div>
          {stats.recent.length === 0 ? (
            <div className="text-sm text-zinc-500 italic">
              Nothing yet. Once the webhook is configured, events will land here.
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {stats.recent.map((r) => (
                <Link key={r.event_id}
                  to={`/clients/${clientId}/product-review/${r.event_id}`}
                  className="block py-2.5 text-sm hover:bg-zinc-50/0 cursor-pointer"
                  data-testid={`pr-recent-row-${r.event_id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      {Array.from({ length: r.rating }).map((_, i) => (
                        <Star key={`${r.event_id}-star-${i}`} className="h-3 w-3 text-amber-500 fill-amber-500" />
                      ))}
                      <span className="font-semibold">{r.author_display_name}</span>
                      {r.listing_full_name && (
                        <span className="font-mono text-[10px] text-zinc-500 truncate max-w-[200px]">
                          on {r.listing_full_name}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {r.review_url && (
                        <a href={r.review_url} target="_blank" rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-[11px] text-zinc-500 hover:text-[#002FA7] inline-flex items-center gap-0.5"
                          title="Open on ProductReview.com.au">
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                      <span className="text-[11px] text-[#002FA7] hover:underline inline-flex items-center gap-0.5">
                        Details <ChevronRight className="h-3 w-3" />
                      </span>
                    </div>
                  </div>
                  {r.review_body && (
                    <div className="text-[12px] text-zinc-700 mt-1 line-clamp-3">
                      {r.review_body}
                    </div>
                  )}
                  <div className="flex items-center gap-3 mt-1 text-[10px] font-mono text-zinc-400">
                    <span>{new Date(r.submitted_at || r.received_at).toLocaleString()}</span>
                    {r.invoice_number && <span>invoice #{r.invoice_number}</span>}
                    {r.query_string_parameters?.cmegi && (
                      <span>via {r.query_string_parameters.cmegi}</span>
                    )}
                    {(r.follow_ups || []).length > 0 && (
                      <span className="text-emerald-600">+{r.follow_ups.length} follow-up{r.follow_ups.length === 1 ? "" : "s"}</span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KPI({ label, value, icon, testid, delta, prev, isRating }) {
  const hasDelta = delta !== null && delta !== undefined && !Number.isNaN(Number(delta));
  const deltaNum = Number(delta);
  const up = deltaNum > 0;
  const down = deltaNum < 0;
  return (
    <div className="rounded-sm border border-zinc-200 bg-white p-4" data-testid={testid}>
      <div className="label-overline">{label}</div>
      <div className="font-display font-extrabold text-3xl tracking-tight mt-1 flex items-center gap-2">
        {icon} <span className="tabular-nums">{value}</span>
      </div>
      {hasDelta && Number(prev) > 0 && (
        <div className={`text-[10px] font-mono mt-1 inline-flex items-center gap-1 ${
          up ? "text-emerald-600" : down ? "text-red-600" : "text-zinc-500"
        }`}>
          {up ? <TrendingUp className="h-3 w-3" />
             : down ? <TrendingDown className="h-3 w-3" />
             : <Minus className="h-3 w-3" />}
          {up ? "+" : ""}{isRating ? deltaNum.toFixed(2) : deltaNum} vs prev
        </div>
      )}
    </div>
  );
}

function TrendBanner({ currentAvg, previousAvg, currentCount, previousCount }) {
  const ratingDelta = currentAvg - previousAvg;
  const countDelta = currentCount - previousCount;
  const improving = ratingDelta > 0.05;
  const declining = ratingDelta < -0.05;
  const stable = !improving && !declining;
  const tone = improving ? "emerald" : declining ? "red" : "zinc";
  const icon = improving ? <TrendingUp className="h-4 w-4" />
             : declining ? <TrendingDown className="h-4 w-4" />
             : <Minus className="h-4 w-4" />;
  const headline = improving
    ? `Quality is improving — average rating up ${Math.abs(ratingDelta).toFixed(2)} stars vs the previous period`
    : declining
    ? `Quality is declining — average rating down ${Math.abs(ratingDelta).toFixed(2)} stars vs the previous period`
    : "Quality is steady — rating unchanged vs the previous period";
  const sub = `Volume ${countDelta >= 0 ? "+" : ""}${countDelta} reviews (${previousCount} → ${currentCount}) vs previous window of equal length.`;
  return (
    <div className={`rounded-sm border p-3 flex items-start gap-2 ${
      tone === "emerald" ? "border-emerald-200 bg-emerald-50 text-emerald-900"
      : tone === "red" ? "border-red-200 bg-red-50 text-red-900"
      : "border-zinc-200 bg-zinc-50 text-zinc-700"
    }`} data-testid="pr-trend-banner">
      <div className={`mt-0.5 ${
        tone === "emerald" ? "text-emerald-700"
        : tone === "red" ? "text-red-700" : "text-zinc-500"
      }`}>{icon}</div>
      <div className="text-[12px] flex-1">
        <div className="font-semibold">{headline}</div>
        <div className="opacity-80 mt-0.5">{sub}</div>
      </div>
    </div>
  );
}
